import {
  createSlice,
  createEntityAdapter,
  createAsyncThunk,
  createSelector,
} from '@reduxjs/toolkit';
import axios from 'axios';
import { RootState, ThunkApi } from 'state/store';
import { Status } from 'utils/customTypes';
import { SLICE_STATUS } from 'utils/constants';
import {
  ProjectOwner,
  FilterParams,
  SortParams,
} from 'types/store/projectOwners';
import { fetchProjectOwnersList } from 'api/projectOwners';
import { buildJsonApiUrl, deserialize } from './helpers';

interface ProjectOwnersState {
  filters?: FilterParams;
  status?: Status;
  totalCount?: number;
  sort?: SortParams;
  links?: {
    next?: string;
  };
  page?: {
    limit: number;
    cursor?: string;
  };
  selectedProjetOwnerIds?: string[];
}

const initialState: ProjectOwnersState = {};

const adapter = createEntityAdapter<ProjectOwner>();

export interface GetProjectOwnersListParams {
  includeTotalCount?: boolean;
}

interface GetProjectOwnersListResponse {
  data: ProjectOwner[];
  meta?: Record<string, string | number>;
  links?: Record<string, string>;
}

interface GetSelectedProjectOwnersParams {
  selected: string[];
}

interface GetSelectedProjectOwnersResponse {
  data: ProjectOwner[];
  selected: string[];
}

/* ============================== REDUX THUNK =============================== */
function concurrencyCondition(
  thunkParameters: unknown,
  { getState }: { getState: () => RootState }
): boolean {
  const { status } = getState().projectOwners;
  return status !== SLICE_STATUS.LOADING;
}

export const getProjectOwnersList = createAsyncThunk<
  GetProjectOwnersListResponse,
  GetProjectOwnersListParams,
  ThunkApi
>(
  'projectOwners/getProjectOwnersList',
  async (params: GetProjectOwnersListParams, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const projectOwnersState = state.projectOwners;
      const { links, filters, sort, page } = projectOwnersState;
      const { includeTotalCount } = params;
      let url = '';
      if (links?.next) {
        url = links.next;
      } else {
        url = buildJsonApiUrl({
          filters,
          sort,
          page,
        });
      }
      const response = await fetchProjectOwnersList({
        url,
        includeTotalCount,
      });
      const deserializedResponse = await deserialize(response);
      return {
        data: deserializedResponse,
        meta: response.meta,
        links: response.links,
      };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data);
      }
      return rejectWithValue(error);
    }
  },
  { condition: concurrencyCondition }
);

export const getSelectedProjectOwners = createAsyncThunk<
  GetSelectedProjectOwnersResponse,
  GetSelectedProjectOwnersParams,
  ThunkApi
>(
  'projectOwners/getSelectedProjectOwners',
  async ({ selected }, { rejectWithValue, getState }) => {
    try {
      const state = getState() as RootState;
      const allProjectOwnerIds = state.projectOwners.ids as string[];
      const projectOwnerIdsToFetch = selected.filter(
        (id) => !allProjectOwnerIds.includes(id)
      );
      const url = buildJsonApiUrl({
        filters: {
          id: projectOwnerIdsToFetch,
        },
        page: {
          limit: 20,
        },
      });
      const response = await fetchProjectOwnersList({ url });
      const deserializedResponse = await deserialize(response);
      return {
        data: deserializedResponse,
        selected,
      };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data);
      }
      return rejectWithValue(error);
    }
  }
);

/* ================================= REDUCER ================================ */
const projectOwnersSlice = createSlice({
  name: 'normalized/projectOwners',
  initialState: adapter.getInitialState(initialState),
  reducers: {
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
    updateFilters: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
    },
    setSelectedProjetOwnerIds: (state, action) => {
      state.selectedProjetOwnerIds = action.payload;
    },
    resetState: (state) => {
      state.filters = undefined;
      state.status = undefined;
      state.totalCount = undefined;
      state.sort = undefined;
      state.links = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProjectOwnersList.pending, (state) => {
        state.status = SLICE_STATUS.LOADING;
      })
      .addCase(getProjectOwnersList.fulfilled, (state, action) => {
        const { links, data, meta } = action.payload;
        adapter.upsertMany(state, data);
        state.links = links;
        if (meta?.totalCount) {
          state.totalCount = meta.totalCount as number;
        }
        state.status = SLICE_STATUS.SUCCESS;
      })
      .addCase(getProjectOwnersList.rejected, (state) => {
        state.status = SLICE_STATUS.FAILED;
      })
      .addCase(getSelectedProjectOwners.fulfilled, (state, action) => {
        adapter.upsertMany(state, action.payload.data);
        state.selectedProjetOwnerIds = action.payload.selected;
      });
  },
});

export const { selectAll: selectAllProjectOwners } =
  adapter.getSelectors<RootState>((state) => state.projectOwners);

export const selectProjectOwnersOptions = createSelector(
  [selectAllProjectOwners],
  (owners: ProjectOwner[]) =>
    owners.map((owner) => ({
      label: owner.fullName,
      value: owner.id,
      avatar: {
        name: owner.fullName,
        imageSrc: owner.avatarUrl || undefined,
        initial: owner.fullName
          .split(' ')
          .map((n) => n[0])
          .join(''),
      },
      disabled: owner.isDisabled,
    }))
);

export const selectProjectOwnersStatus = (state: RootState) =>
  state.projectOwners.status;

export const selectCanGetMoreProjectOwners = (state: RootState) =>
  state.projectOwners.links?.next ? true : false;

export const selectSelectedProjectOwnerIds = (state: RootState) =>
  state.projectOwners.selectedProjetOwnerIds || [];

export const selectSelectedProjectOwners = createSelector(
  [selectSelectedProjectOwnerIds, selectAllProjectOwners],
  (selectedProjectOwnerIds: string[], allProjectOwners: ProjectOwner[]) => {
    return allProjectOwners.filter((owner) =>
      selectedProjectOwnerIds.includes(owner.id)
    );
  }
);

export const {
  setFilters,
  updateFilters,
  setSelectedProjetOwnerIds,
  resetState,
} = projectOwnersSlice.actions;

export default projectOwnersSlice.reducer;
